import { FilterMappingEntry } from '../../interfaces/api/report.interface';
import { TranslationsPerLanguage } from '../../interfaces/ui/language.interface';
import {
  ExportColumnConfig,
  NgxDatatableColumn,
} from '../../interfaces/ui/ngx-datatable.interface';
import {
  BadgeStyleConfig,
  NgSelect,
  TranslatedSelectOption,
} from '../../interfaces/ui/ui.interface';
import { formatNumberTwoDecimals } from './two-decimals-global-tables.utils';

export enum BET_TYPE {
  SIMPLE = 'Simple',
  MULTIPLE = 'Multiple',
  SYSTEM = 'System',
  CUSTOM = 'Custom',
}

export const OPTION_STATUS_YES_NO = [
  {
    value: true,
    label: 'words.yes',
  },
  {
    value: false,
    label: 'words.no',
  },
];

const BET_TYPE_TRANSLATIONS: Record<BET_TYPE, TranslationsPerLanguage> = {
  [BET_TYPE.SIMPLE]: { es: 'Simple', en: 'Simple' },
  [BET_TYPE.MULTIPLE]: { es: 'Multiple', en: 'Multiple' },
  [BET_TYPE.SYSTEM]: { es: 'Sistema', en: 'System' },
  [BET_TYPE.CUSTOM]: { es: 'Personalizada', en: 'Custom' },
};

export const BET_TYPE_BADGE_OPTIONS: Record<string, BadgeStyleConfig> = {
  [BET_TYPE.SIMPLE]: {
    ...BET_TYPE_TRANSLATIONS[BET_TYPE.SIMPLE],
    color: '#8f8f8f',
  },
  [BET_TYPE.MULTIPLE]: {
    ...BET_TYPE_TRANSLATIONS[BET_TYPE.MULTIPLE],
    color: '#8f8f8f',
  },
  [BET_TYPE.SYSTEM]: {
    ...BET_TYPE_TRANSLATIONS[BET_TYPE.SYSTEM],
    color: '#8f8f8f',
  },
  [BET_TYPE.CUSTOM]: {
    ...BET_TYPE_TRANSLATIONS[BET_TYPE.CUSTOM],
    color: '#8f8f8f',
  },
};

export const BET_TYPE_SELECT_OPTIONS: TranslatedSelectOption<string>[] = [
  {
    label: BET_TYPE_TRANSLATIONS[BET_TYPE.SIMPLE],
    value: BET_TYPE.SIMPLE,
  },
  {
    label: BET_TYPE_TRANSLATIONS[BET_TYPE.MULTIPLE],
    value: BET_TYPE.MULTIPLE,
  },
  {
    label: BET_TYPE_TRANSLATIONS[BET_TYPE.SYSTEM],
    value: BET_TYPE.SYSTEM,
  },
  {
    label: BET_TYPE_TRANSLATIONS[BET_TYPE.CUSTOM],
    value: BET_TYPE.CUSTOM,
  },
];

export enum BET_RESULT {
  'NOT_RESULTED' = 'Not Resulted',
  'WON' = 'Won',
  'CASHOUT' = 'Cashout',
  'LOST' = 'Lost',
  'CANCEL' = 'Cancel',
  'CANCELED' = 'Canceled',
  'REFUND' = 'Refund',
}

const BET_RESULT_TRANSLATIONS: Record<BET_RESULT, TranslationsPerLanguage> = {
  [BET_RESULT.NOT_RESULTED]: { es: 'No resuelto', en: 'Not resulted' },
  [BET_RESULT.WON]: { es: 'Ganada', en: 'Won' },
  [BET_RESULT.CASHOUT]: { es: 'Cashout', en: 'Cashout' },
  [BET_RESULT.LOST]: { es: 'Perdida', en: 'Lost' },
  [BET_RESULT.CANCEL]: { es: 'Cancelada', en: 'Canceled' },
  [BET_RESULT.CANCELED]: { es: 'Cancelada por el sistema', en: 'Canceled by the system' },
  [BET_RESULT.REFUND]: { es: 'Devolución por el sistema', en: 'Refund by the system' },
};

export const BET_RESULT_SELECT_OPTIONS: TranslatedSelectOption<string>[] = Object.values(
  BET_RESULT
).map((result) => ({
  label: BET_RESULT_TRANSLATIONS[result],
  value: result,
}));

const BET_RESULT_COLORS: Record<BET_RESULT, string> = {
  [BET_RESULT.NOT_RESULTED]: '#FF7F50',
  [BET_RESULT.WON]: '#32CD32',
  [BET_RESULT.CASHOUT]: '#FEB500',
  [BET_RESULT.LOST]: '#FF0000',
  [BET_RESULT.CANCEL]: '#D02090',
  [BET_RESULT.CANCELED]: '#D02090',
  [BET_RESULT.REFUND]: '#D02090',
};

export const BET_RESULT_BADGE_OPTIONS: Record<string, BadgeStyleConfig> = Object.fromEntries(
  Object.values(BET_RESULT).map((result) => [
    result,
    { ...BET_RESULT_TRANSLATIONS[result], color: BET_RESULT_COLORS[result] },
  ])
);

export enum VOID_FACTOR {
  NONE = '0',
  HALF = '0.5',
  FULL = '1',
}

const VOID_FACTOR_TRANSLATIONS: Record<VOID_FACTOR, TranslationsPerLanguage> = {
  [VOID_FACTOR.NONE]: { es: '', en: '' },
  [VOID_FACTOR.HALF]: { es: 'Devolución parcial', en: 'Partial refund' },
  [VOID_FACTOR.FULL]: { es: 'Devuelta', en: 'Refund' },
};

export const VOID_FACTOR_BADGE_OPTIONS: Record<string, BadgeStyleConfig> = {
  [VOID_FACTOR.HALF]: {
    ...VOID_FACTOR_TRANSLATIONS[VOID_FACTOR.HALF],
    color: '#D02090',
  },
  [VOID_FACTOR.FULL]: {
    ...VOID_FACTOR_TRANSLATIONS[VOID_FACTOR.FULL],
    color: '#D02090',
  },
};

/**
 * DEPRECATED — flattened cross product of `voidFactor` and result.
 */
export enum BET_EVENT_RESULT {
  '0-NOT_RESULTED' = '0-Not Resulted',
  '0-CANCELED' = '0-Canceled',
  '0-REFUND' = '0-Refund',
  '0-CANCEL' = '0-Cancel',
  '0-CASHOUT' = '0-Cashout',
  '0-LOST' = '0-Lost',
  '0-WON' = '0-Won',
  '1-LOST' = '1-Lost',
  '0.5-LOST' = '0.5-Lost',
  '0.5-WON' = '0.5-Won',
}

const BET_EVENT_RESULT_TRANSLATIONS: Record<BET_EVENT_RESULT, TranslationsPerLanguage> = {
  [BET_EVENT_RESULT['0-NOT_RESULTED']]: {
    es: 'No resuelto',
    en: 'Not resulted',
  },
  [BET_EVENT_RESULT['0-CANCELED']]: {
    es: 'Cancelada por el sistema',
    en: 'Canceled by the system',
  },
  [BET_EVENT_RESULT['0-REFUND']]: {
    es: 'Devolución por el sistema',
    en: 'Refund by the system',
  },
  [BET_EVENT_RESULT['0-CANCEL']]: {
    es: 'Cancelada',
    en: 'Canceled',
  },
  [BET_EVENT_RESULT['0-CASHOUT']]: {
    es: 'Cashout',
    en: 'Cashout',
  },
  [BET_EVENT_RESULT['0-LOST']]: {
    es: 'Perdida',
    en: 'Lost',
  },
  [BET_EVENT_RESULT['0-WON']]: {
    es: 'Ganada',
    en: 'Won',
  },
  [BET_EVENT_RESULT['1-LOST']]: {
    es: 'Devuelta',
    en: 'Refund',
  },
  [BET_EVENT_RESULT['0.5-LOST']]: {
    es: 'Perdida parcialmente',
    en: 'Lost partially',
  },
  [BET_EVENT_RESULT['0.5-WON']]: {
    es: 'Ganada parcialmente',
    en: 'Won partially',
  },
};

export const BET_EVENT_RESULT_BADGE_OPTIONS: Record<string, BadgeStyleConfig> = {
  [BET_EVENT_RESULT['0-NOT_RESULTED']]: {
    ...BET_EVENT_RESULT_TRANSLATIONS[BET_EVENT_RESULT['0-NOT_RESULTED']],
    color: '#FF7F50',
  },
  [BET_EVENT_RESULT['0-CANCELED']]: {
    ...BET_EVENT_RESULT_TRANSLATIONS[BET_EVENT_RESULT['0-CANCELED']],
    color: '#D02090',
  },
  [BET_EVENT_RESULT['0-REFUND']]: {
    ...BET_EVENT_RESULT_TRANSLATIONS[BET_EVENT_RESULT['0-REFUND']],
    color: '#D02090',
  },
  [BET_EVENT_RESULT['0-CANCEL']]: {
    ...BET_EVENT_RESULT_TRANSLATIONS[BET_EVENT_RESULT['0-CANCEL']],
    color: '#D02090',
  },
  [BET_EVENT_RESULT['0-CASHOUT']]: {
    ...BET_EVENT_RESULT_TRANSLATIONS[BET_EVENT_RESULT['0-CASHOUT']],
    color: '#FEB500',
  },
  [BET_EVENT_RESULT['0-LOST']]: {
    ...BET_EVENT_RESULT_TRANSLATIONS[BET_EVENT_RESULT['0-LOST']],
    color: '#FF0000',
  },
  [BET_EVENT_RESULT['0-WON']]: {
    ...BET_EVENT_RESULT_TRANSLATIONS[BET_EVENT_RESULT['0-WON']],
    color: '#32CD32',
  },
  [BET_EVENT_RESULT['1-LOST']]: {
    ...BET_EVENT_RESULT_TRANSLATIONS[BET_EVENT_RESULT['1-LOST']],
    color: '#D02090',
  },
  [BET_EVENT_RESULT['0.5-LOST']]: {
    ...BET_EVENT_RESULT_TRANSLATIONS[BET_EVENT_RESULT['0.5-LOST']],
    color: '#32CD32',
  },
  [BET_EVENT_RESULT['0.5-WON']]: {
    ...BET_EVENT_RESULT_TRANSLATIONS[BET_EVENT_RESULT['0.5-WON']],
    color: '#32CD32',
  },
};

export enum BET_EVENT_STATUS {
  LIVE = 'Live',
  NOT_STARTED = 'NotStarted',
}

const BET_EVENT_STATUS_TRANSLATIONS: Record<BET_EVENT_STATUS, TranslationsPerLanguage> = {
  [BET_EVENT_STATUS.NOT_STARTED]: { es: 'No iniciado', en: 'Not started' },
  [BET_EVENT_STATUS.LIVE]: { es: 'En vivo', en: 'Live' },
};

export const BET_EVENT_STATUS_BADGE_OPTIONS: Record<string, BadgeStyleConfig> = {
  [BET_EVENT_STATUS.LIVE]: {
    ...BET_EVENT_STATUS_TRANSLATIONS[BET_EVENT_STATUS.LIVE],
    color: '#FF0000',
  },
  [BET_EVENT_STATUS.NOT_STARTED]: {
    ...BET_EVENT_STATUS_TRANSLATIONS[BET_EVENT_STATUS.NOT_STARTED],
    color: '#FFA500',
  },
};

export enum BET_PLATFORM {
  WEB = 'Web',
  MOBILE = 'Mobile',
}

const BET_PLATFORM_TRANSLATIONS: Record<BET_PLATFORM, TranslationsPerLanguage> = {
  [BET_PLATFORM.WEB]: { es: 'Web', en: 'Web' },
  [BET_PLATFORM.MOBILE]: { es: 'Móvil', en: 'Mobile' },
};

export const BET_PLATFORM_BADGE_OPTIONS: Record<string, BadgeStyleConfig> = {
  [BET_PLATFORM.WEB]: {
    ...BET_PLATFORM_TRANSLATIONS[BET_PLATFORM.WEB],
    color: '#4A90E2',
  },
  [BET_PLATFORM.MOBILE]: {
    ...BET_PLATFORM_TRANSLATIONS[BET_PLATFORM.MOBILE],
    color: '#50E3C2',
  },
};

export const BET_PLATFORM_SELECT_OPTIONS: TranslatedSelectOption<string>[] = [
  {
    label: BET_PLATFORM_TRANSLATIONS[BET_PLATFORM.WEB],
    value: BET_PLATFORM.WEB,
  },
  {
    label: BET_PLATFORM_TRANSLATIONS[BET_PLATFORM.MOBILE],
    value: BET_PLATFORM.MOBILE,
  },
];

export enum BETTING_REPORT_DATE_FIELD {
  CREATED_AT = 'createdAt',
  SETTLED_AT = 'settledAt',
}

export const BETTING_REPORT_DATE_FIELD_SELECT_OPTIONS: TranslatedSelectOption<string>[] = [
  {
    label: { es: 'Fecha de creación', en: 'Creation date' },
    value: BETTING_REPORT_DATE_FIELD.CREATED_AT,
  },
  {
    label: { es: 'Fecha de liquidación', en: 'Settlement date' },
    value: BETTING_REPORT_DATE_FIELD.SETTLED_AT,
  },
];

export const EXACT_GGR_PRESET_CONTROLS = ['dateField', 'isBonus', 'result'] as const;

export enum BETTING_NUMERIC_FIELD {
  NONE = '',
  AMOUNT = 'amountBet',
  ODD = 'oddsTotal',
  GAIN = 'totalGain',
  STAKE = 'stake',
  PAYMENT = 'payment',
  GGR = 'ggr',
}

export const BETTING_NUMERIC_FIELD_OPTIONS: NgSelect<BETTING_NUMERIC_FIELD>[] = [
  {
    label: 'words.none',
    value: BETTING_NUMERIC_FIELD.NONE,
  },
  {
    label: 'reports.sportsBetting.stake',
    value: BETTING_NUMERIC_FIELD.AMOUNT,
  },
  {
    label: 'reports.sportsBetting.odd',
    value: BETTING_NUMERIC_FIELD.ODD,
  },
  {
    label: 'reports.sportsBetting.winning',
    value: BETTING_NUMERIC_FIELD.GAIN,
  },
];

export enum SPORT_BETTING {
  EXPANDABLE = 'expandable',
  BET_ID = 'betId',
  PLAYER_ID = 'playerId',
  PLAYER_USERNAME = 'username',
  TYPE = 'type',
  RESULT = 'result',
  CURRENCY = 'currency',
  STAKE = 'stake',
  ODD = 'odd',
  WINNING = 'winning',
  GGR = 'ggr',
  IS_BONUS = 'isBonus',
  IP_ADDRESS = 'ipAddress',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  SOURCE = 'source',
  EVENT_QUANTITY = 'eventQuantity',
  POSIBLE_WIN = 'posibleWin',
  CREATED_AT = 'createdAt',
  SETTLED_AT = 'settledAt',
  AGENT_USERNAME = 'agentUsername',
  ACTIONS = 'actions',
}

export const SPORT_BETTING_COLUMNS: Partial<Record<SPORT_BETTING, Partial<NgxDatatableColumn>>> = {
  [SPORT_BETTING.EXPANDABLE]: {
    frozenLeft: true,
    isExpandable: true,
    width: 50,
  },
  [SPORT_BETTING.BET_ID]: {
    name: 'reports.sportsBetting.betId',
    prop: 'betLocalId',
    sortable: true,
    width: 150,
  },
  [SPORT_BETTING.PLAYER_ID]: {
    name: 'reports.sportsBetting.playerId',
    prop: 'player.playerId',
    isMobileSubtitle: true,
    width: 125,
  },
  [SPORT_BETTING.PLAYER_USERNAME]: {
    name: 'reports.sportsBetting.playerUsername',
    prop: 'player.username',
    isMobileTitle: true,
    width: 175,
  },
  [SPORT_BETTING.TYPE]: {
    name: 'reports.sportsBetting.type',
    width: 125,
  },
  [SPORT_BETTING.RESULT]: {
    name: 'reports.sportsBetting.result',
    width: 175,
  },
  [SPORT_BETTING.CURRENCY]: {
    name: 'words.currency',
    prop: 'currency',
    width: 100,
  },
  [SPORT_BETTING.ODD]: {
    name: 'reports.sportsBetting.odd',
    headerClass: 'text-end',
    cellClass: 'text-end',
    width: 100,
  },
  [SPORT_BETTING.STAKE]: {
    name: 'reports.sportsBetting.stake',
    headerClass: 'text-end',
    cellClass: 'text-end',
    prop: 'amountBet',
    sortable: true,
    width: 100,
  },
  [SPORT_BETTING.POSIBLE_WIN]: {
    name: 'reports.sportsBetting.possibleWinning',
    headerClass: 'text-end',
    cellClass: 'text-end',
    width: 140,
  },
  [SPORT_BETTING.WINNING]: {
    name: 'reports.sportsBetting.winning',
    headerClass: 'text-end',
    cellClass: 'text-end',
    width: 100,
  },
  [SPORT_BETTING.GGR]: {
    name: 'GGR',
    headerClass: 'text-end',
    cellClass: 'text-end',
    width: 100,
  },
  [SPORT_BETTING.IS_BONUS]: {
    name: 'reports.sportsBetting.isBonus',
    width: 100,
  },
  [SPORT_BETTING.IP_ADDRESS]: {
    name: 'reports.sportsBetting.ipAddress',
    prop: 'ipAddress',
    width: 150,
  },
  [SPORT_BETTING.FIRST_NAME]: {
    name: 'reports.sportsBetting.firstName',
    prop: 'player.firstName',
    width: 125,
  },
  [SPORT_BETTING.LAST_NAME]: {
    name: 'reports.sportsBetting.lastName',
    prop: 'player.lastName',
    width: 125,
  },
  [SPORT_BETTING.SOURCE]: {
    name: 'reports.sportsBetting.source',
    width: 100,
  },
  [SPORT_BETTING.EVENT_QUANTITY]: {
    name: 'reports.sportsBetting.eventQuantity',
    width: 140,
  },
  [SPORT_BETTING.AGENT_USERNAME]: {
    name: 'reports.sportsBetting.agentUsername',
    width: 150,
    prop: 'agent.username',
  },
  [SPORT_BETTING.CREATED_AT]: {
    name: 'words.createdAt',
    prop: 'createdAt',
    sortable: true,
  },
  [SPORT_BETTING.SETTLED_AT]: {
    name: 'words.settledAt',
    prop: 'settledAt',
  },
  [SPORT_BETTING.ACTIONS]: {
    name: 'words.actions',
    frozenRight: true,
    width: 100,
  },
};

export const SPORT_BETTING_COLUMNS_EXPORT: ExportColumnConfig[] = [
  {
    label: 'words.createdAt',
    formattedDataKey: 'createdAt',
  },
  {
    label: 'reports.sportsBetting.betId',
    formattedDataKey: 'betLocalId',
  },
  {
    label: 'reports.sportsBetting.playerId',
    formattedDataKey: 'playerId',
  },
  {
    label: 'reports.sportsBetting.playerUsername',
    formattedDataKey: 'username',
  },
  {
    label: 'reports.sportsBetting.type',
    formattedDataKey: 'betType',
  },
  {
    label: 'reports.sportsBetting.source',
    formattedDataKey: 'source',
  },
  {
    label: 'reports.sportsBetting.sport',
    formattedDataKey: 'sport',
  },
  {
    label: 'reports.sportsBetting.category',
    formattedDataKey: 'category',
  },
  {
    label: 'reports.sportsBetting.tournament',
    formattedDataKey: 'tournament',
  },
  {
    label: 'reports.sportsBetting.event',
    formattedDataKey: 'event',
  },
  {
    label: 'reports.sportsBetting.market',
    formattedDataKey: 'market',
  },
  {
    label: 'reports.sportsBetting.selection',
    formattedDataKey: 'selection',
  },
  {
    label: 'reports.sportsBetting.odd',
    formattedDataKey: 'eventOdd',
  },
  {
    label: 'reports.sportsBetting.result',
    formattedDataKey: 'eventResult',
  },
];

export const BONUS_PROMOTIONAL_CODES_COLUMNS_EXPORT: ExportColumnConfig[] = [
  {
    label: 'reports.bonus.playerBonusId',
    formattedDataKey: 'playerPromotionalCodeId',
  },
  {
    label: 'reports.casino.playerId',
    formattedDataKey: 'externalPlayerId',
  },
  {
    label: 'financial.holders.player',
    formattedDataKey: 'playerUsername',
  },
  {
    label: 'reports.bonus.agentExternalId',
    formattedDataKey: 'agentExternalId',
  },
  {
    label: 'reports.bonus.agency',
    formattedDataKey: 'agentName',
  },
  {
    label: 'reports.bonus.bonusId',
    formattedDataKey: 'bonusId',
  },
  {
    label: 'reports.bonus.typeBonus',
    formattedDataKey: 'typeBonus',
  },
  {
    label: 'reports.bonus.bonusName',
    formattedDataKey: 'bonusName',
  },
  {
    label: 'reports.bonus.code',
    formattedDataKey: 'bonusCode',
  },
  {
    label: 'reports.bonus.createdAt',
    formattedDataKey: 'bonusCreationDate',
  },
  {
    label: 'reports.bonus.acceptanceDate',
    formattedDataKey: 'activationDate',
  },
  {
    label: 'reports.bonus.bonusAmount',
    formattedDataKey: 'amount',
  },
  {
    label: 'reports.bonus.amountDeposited',
    formattedDataKey: 'amountDeposited',
  },
  {
    label: 'reports.bonus.amountBonusPaid',
    formattedDataKey: 'amountBonusPaid',
  },
  {
    label: 'reports.bonus.amountBonusGenerated',
    formattedDataKey: 'amountBonusGenerated',
  },
  {
    label: 'reports.bonus.bonusStatus',
    formattedDataKey: 'state',
  },
  {
    label: 'reports.bonus.dateOfResult',
    formattedDataKey: 'resultDate',
  },
  {
    label: 'reports.bonus.stateResult',
    formattedDataKey: 'result',
  },
  {
    label: 'reports.bonus.expirationDate',
    formattedDataKey: 'expirationDate',
  },
  {
    label: 'reports.transactions.currencyId',
    formattedDataKey: 'currecyCode',
  },
  {
    label: 'reports.bonus.wasDeposit',
    formattedDataKey: 'wasDeposit',
  },
  {
    label: 'reports.bonus.playerCategory',
    formattedDataKey: 'playerCategory',
  },
  {
    label: 'reports.bonus.product',
    formattedDataKey: 'product',
  },
  {
    label: 'reports.bonus.claimDate',
    formattedDataKey: 'bonusClaimDate',
  },
  {
    label: 'reports.bonus.updateBonusRegisterDate',
    formattedDataKey: 'updatedAt',
  },
  {
    label: 'reports.bonus.reasonCancellation',
    formattedDataKey: 'reasonCancellation',
  },
];

export const BONUS_PROMOTIONAL_CODES_COLUMNS = [
  {
    name: 'reports.bonus.playerBonusId',
    prop: 'playerPromotionalCodeId',
    width: 150,
  },

  {
    name: 'reports.casino.playerId',
    prop: 'externalPlayerId',
    width: 150,
  },
  {
    name: 'financial.holders.player',
    prop: 'playerUsername',
    width: 100,
  },
  {
    name: 'reports.bonus.agentExternalId',
    prop: 'agentExternalId',
    width: 150,
  },
  {
    name: 'reports.bonus.agency',
    prop: 'agentName',
    width: 150,
  },
  {
    name: 'reports.bonus.bonusId',
    prop: 'bonusId',
    width: 150,
  },
  {
    name: 'reports.bonus.typeBonus',
    prop: 'typeBonus',
    width: 150,
  },
  {
    name: 'reports.bonus.bonusName',
    prop: 'bonusName',
    width: 250,
  },
  {
    name: 'reports.bonus.code',
    prop: 'bonusCode',
    width: 300,
  },
];

export enum GROUPED_SPORT_BETTING {
  LINKED = 'linked',
  SPORT = 'sport',
  CATEGORY = 'category',
  TOURNAMENT = 'tournament',
  EVENT = 'event',
  MARKET = 'market',
  AMOUNT = 'amount',
  GAIN = 'gain',
  PROFIT_LOSS = 'profitLoss',
  UTILITY_PERCENT = 'utilityPercentage',
}

export const GROUPED_SPORT_BETTING_COLUMNS: Partial<
  Record<GROUPED_SPORT_BETTING, Partial<NgxDatatableColumn>>
> = {
  [GROUPED_SPORT_BETTING.LINKED]: { width: 90 },
  [GROUPED_SPORT_BETTING.SPORT]: {
    name: 'reports.sportsBetting.sport',
  },
  [GROUPED_SPORT_BETTING.CATEGORY]: {
    name: 'reports.sportsBetting.category',
  },
  [GROUPED_SPORT_BETTING.TOURNAMENT]: {
    name: 'reports.sportsBetting.tournament',
  },
  [GROUPED_SPORT_BETTING.EVENT]: {
    name: 'reports.sportsBetting.event',
  },
  [GROUPED_SPORT_BETTING.MARKET]: {
    name: 'reports.sportsBetting.market',
  },
  [GROUPED_SPORT_BETTING.AMOUNT]: {
    name: 'reports.sportsBetting.amount',
    shouldSum: true,
    valueFormatter: formatNumberTwoDecimals,
    shouldCalculateUtilityAmount: true,
    prop: 'totalAmount',
  },
  [GROUPED_SPORT_BETTING.GAIN]: {
    name: 'reports.sportsBetting.gain',
    shouldSum: true,
    valueFormatter: formatNumberTwoDecimals,
    shouldCalculateUtilityGain: true,
    prop: 'totalGain',
  },
  [GROUPED_SPORT_BETTING.PROFIT_LOSS]: {
    name: 'reports.sportsBetting.profitLoss',
    shouldSum: true,
    valueFormatter: formatNumberTwoDecimals,
    prop: 'totalNetGain',
  },
  [GROUPED_SPORT_BETTING.UTILITY_PERCENT]: {
    name: 'reports.sportsBetting.utility',
    prop: 'utilityPercentage',
    shouldCalculateUtility: true,
  },
};

export const ALL_OPTION: TranslatedSelectOption<string> = {
  label: { en: 'All', es: 'Todo' },
  value: '',
};

export const ALL_OPTION_TRANSLATE_PIPE: NgSelect<string> = {
  label: 'words.all',
  value: '',
};

export const TRANSACTION_ADMIN_COLUMNS = [
  {
    name: 'reports.transactions.usernamePlayer',
    prop: 'userData',
    width: 200,
  },
];

export enum TRASACTION_STATUS {
  PROCESSED = 'Processed',
  PENDING = 'Pending',
  REJECTED = 'Rejected',
}

export const TRASACTION_STATUS_TRANSLATIONS: Record<TRASACTION_STATUS, TranslationsPerLanguage> = {
  [TRASACTION_STATUS.PROCESSED]: { es: 'Procesado', en: 'Processed' },
  [TRASACTION_STATUS.PENDING]: { es: 'Pendiente', en: 'Pending' },
  [TRASACTION_STATUS.REJECTED]: { es: 'Rechazado', en: 'Rejected' },
};

export const TRANSACTION_COLUMN_NAMES = {
  AMOUNT: 'reports.transactions.amount',
  OPERATION_TYPE: 'reports.transactions.operationType',
  ORIGIN: 'reports.transactions.origin',
  USERNAME_PLAYER: 'reports.transactions.usernamePlayer',
  PREVIOUS_BALANCE: 'reports.transactions.previousBalance',
  CURRENT_BALANCE: 'reports.transactions.currentBalance',
  PREVIOUS_BALANCE_SHORT: 'reports.transactions.previousBalanceShort',
  CURRENT_BALANCE_SHORT: 'reports.transactions.currentBalanceShort',
} as const;

export const TRANSACTION_ORIGIN_COLUMN_WIDTH = 140;

export const AGENT_BALANCE_MOMENTS = {
  PREVIOUS: 'previous',
  CURRENT: 'current',
} as const;

export const AGENT_BALANCE_SOURCES = {
  OWN: 'own',
  REGISTERED: 'registered',
} as const;

export const AGENT_BALANCE_UNCHANGED_REASONS = {
  REJECTED: 'reports.transactions.rejectedBalanceUnchanged',
  NOT_AGENT: 'reports.transactions.agentBalanceUnchanged',
} as const;

export const TRASACTION_STATUS_BADGE_OPTIONS: Record<string, BadgeStyleConfig> = {
  [TRASACTION_STATUS.PROCESSED]: {
    ...TRASACTION_STATUS_TRANSLATIONS[TRASACTION_STATUS.PROCESSED],
    color: '#40D933',
  },
  [TRASACTION_STATUS.PENDING]: {
    ...TRASACTION_STATUS_TRANSLATIONS[TRASACTION_STATUS.PENDING],
    color: '#FF6F61',
  },
  [TRASACTION_STATUS.REJECTED]: {
    ...TRASACTION_STATUS_TRANSLATIONS[TRASACTION_STATUS.REJECTED],
    color: '#FF0000',
  },
};

export enum MONEY_TYPE {
  CASH = 'Cash',
  CREDIT = 'Credit',
  CORRECTION = 'Correction',
}

export const MONEY_TYPE_TRANSLATIONS: Record<string, TranslationsPerLanguage> = {
  [MONEY_TYPE.CASH]: { es: 'Efectivo', en: 'Cash' },
  [MONEY_TYPE.CREDIT]: { es: 'Crédito', en: 'Credit' },
  [MONEY_TYPE.CORRECTION]: { es: 'Corrección', en: 'Correction' },
};

export const MONEY_TYPE_BADGE_OPTIONS: Record<string, BadgeStyleConfig> = {
  [MONEY_TYPE.CASH]: {
    ...MONEY_TYPE_TRANSLATIONS[MONEY_TYPE.CASH],
    color: '#28A745',
  },
  [MONEY_TYPE.CREDIT]: {
    ...MONEY_TYPE_TRANSLATIONS[MONEY_TYPE.CREDIT],
    color: '#FFC107',
  },
  [MONEY_TYPE.CORRECTION]: {
    ...MONEY_TYPE_TRANSLATIONS[MONEY_TYPE.CORRECTION],
    color: '#6C757D',
  },
};

export enum TRANSACTION_TYPE {
  DEPOSIT = 'Deposit',
  WITHDRAWAL = 'Withdrawal',
}

export const TRANSACTION_TYPE_TRANSLATIONS: Record<string, TranslationsPerLanguage> = {
  [TRANSACTION_TYPE.DEPOSIT]: { es: 'Depósito', en: 'Deposit' },
  [TRANSACTION_TYPE.WITHDRAWAL]: { es: 'Retiro', en: 'Withdrawal' },
};

export const TRANSACTION_TYPE_BADGE_OPTIONS: Record<string, BadgeStyleConfig> = {
  [TRANSACTION_TYPE.DEPOSIT]: {
    ...TRANSACTION_TYPE_TRANSLATIONS[TRANSACTION_TYPE.DEPOSIT],
    color: '#007BFF',
  },
  [TRANSACTION_TYPE.WITHDRAWAL]: {
    ...TRANSACTION_TYPE_TRANSLATIONS[TRANSACTION_TYPE.WITHDRAWAL],
    color: '#DC3545',
  },
};

export enum PAYMENT_TYPE {
  DIRECT = 'Direct',
  BANK_TRANSFER = 'Banking',
}

export const PAYMENT_TYPE_TRANSLATIONS: Record<string, TranslationsPerLanguage> = {
  [PAYMENT_TYPE.DIRECT]: { es: 'Directo', en: 'Direct' },
  [PAYMENT_TYPE.BANK_TRANSFER]: { es: 'Transferencia bancaria', en: 'Banking' },
};

export const PAYMENT_TYPE_BADGE_OPTIONS: Record<string, BadgeStyleConfig> = {
  [PAYMENT_TYPE.DIRECT]: {
    ...PAYMENT_TYPE_TRANSLATIONS[PAYMENT_TYPE.DIRECT],
    color: '#20C997',
  },
  [PAYMENT_TYPE.BANK_TRANSFER]: {
    ...PAYMENT_TYPE_TRANSLATIONS[PAYMENT_TYPE.BANK_TRANSFER],
    color: '#17A2B8',
  },
};

export enum PHYSICAL_WITHDRAW_CODE_STATUS {
  ACTIVE = 'active',
  EXPIRED = 'expired',
  CLAIMED = 'claimed',
  CANCELLED = 'cancelled',
}

export const PHYSICAL_WITHDRAW_CODE_STATUS_TRANSLATIONS: Record<
  PHYSICAL_WITHDRAW_CODE_STATUS,
  TranslationsPerLanguage
> = {
  [PHYSICAL_WITHDRAW_CODE_STATUS.ACTIVE]: { es: 'Activo', en: 'Active' },
  [PHYSICAL_WITHDRAW_CODE_STATUS.EXPIRED]: { es: 'Vencido', en: 'Expired' },
  [PHYSICAL_WITHDRAW_CODE_STATUS.CLAIMED]: { es: 'Cobrado', en: 'Claimed' },
  [PHYSICAL_WITHDRAW_CODE_STATUS.CANCELLED]: { es: 'Cancelado', en: 'Cancelled' },
};

export const PHYSICAL_WITHDRAW_CODE_STATUS_BADGE_OPTIONS: Record<string, BadgeStyleConfig> = {
  [PHYSICAL_WITHDRAW_CODE_STATUS.ACTIVE]: {
    ...PHYSICAL_WITHDRAW_CODE_STATUS_TRANSLATIONS[PHYSICAL_WITHDRAW_CODE_STATUS.ACTIVE],
    color: '#40D933',
  },
  [PHYSICAL_WITHDRAW_CODE_STATUS.EXPIRED]: {
    ...PHYSICAL_WITHDRAW_CODE_STATUS_TRANSLATIONS[PHYSICAL_WITHDRAW_CODE_STATUS.EXPIRED],
    color: '#FF6F61',
  },
  [PHYSICAL_WITHDRAW_CODE_STATUS.CLAIMED]: {
    ...PHYSICAL_WITHDRAW_CODE_STATUS_TRANSLATIONS[PHYSICAL_WITHDRAW_CODE_STATUS.CLAIMED],
    color: '#3498DB',
  },
  [PHYSICAL_WITHDRAW_CODE_STATUS.CANCELLED]: {
    ...PHYSICAL_WITHDRAW_CODE_STATUS_TRANSLATIONS[PHYSICAL_WITHDRAW_CODE_STATUS.CANCELLED],
    color: '#95A5A6',
  },
};

export const PHYSICAL_WITHDRAW_CODE_CANCELLED_BY_TRANSLATIONS: Record<
  'admin' | 'player',
  TranslationsPerLanguage
> = {
  admin: { es: 'Cancelado por el administrador', en: 'Cancelled by administrator' },
  player: { es: 'Cancelado por el jugador', en: 'Cancelled by player' },
};

export const PHYSICAL_WITHDRAW_CODE_STATUS_FILTER = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
  CLAIMED: 'claimed',
  CANCELLED_BY_ADMIN: 'cancelled_by_admin',
  CANCELLED_BY_PLAYER: 'cancelled_by_player',
};

export const PHYSICAL_WITHDRAW_CODE_STATUS_OPTIONS: NgSelect<string>[] = [
  {
    value: PHYSICAL_WITHDRAW_CODE_STATUS_FILTER.ACTIVE,
    label: 'reports.physicalWithdrawCodesHistory.statusActive',
  },
  {
    value: PHYSICAL_WITHDRAW_CODE_STATUS_FILTER.EXPIRED,
    label: 'reports.physicalWithdrawCodesHistory.statusExpired',
  },
  {
    value: PHYSICAL_WITHDRAW_CODE_STATUS_FILTER.CLAIMED,
    label: 'reports.physicalWithdrawCodesHistory.statusClaimed',
  },
  {
    value: PHYSICAL_WITHDRAW_CODE_STATUS_FILTER.CANCELLED_BY_ADMIN,
    label: 'reports.physicalWithdrawCodesHistory.statusCancelledByAdmin',
  },
  {
    value: PHYSICAL_WITHDRAW_CODE_STATUS_FILTER.CANCELLED_BY_PLAYER,
    label: 'reports.physicalWithdrawCodesHistory.statusCancelledByPlayer',
  },
];

export const STATE_TRANSACTION = [
  { value: 'Processed', label: 'reports.transactions.processed' },
  { value: 'Pending', label: 'reports.transactions.pending' },
  { value: 'Rejected', label: 'reports.transactions.rejected' },
  { value: 'Accepted', label: 'reports.transactions.accepted' },
];

export const ORIGIN_TRANSACTION_PLATAFORMS = [
  { value: 'Players', label: 'reports.transactions.players' },
  { value: 'Backoffice', label: 'reports.transactions.backOffice' },
];

export const ROLE_TYPE = [
  { value: 'Agents', label: 'agents.titleSidebar' },
  { value: 'Player', label: 'players.titleSidebar' },
];

export const USER_TYPE_OPTIONS = [
  { value: 'agent', label: 'reports.transactions.agent' },
  { value: 'collaboratorAgent', label: 'reports.transactions.collaboratorAgent' },
  { value: 'collaboratorOperator', label: 'reports.transactions.collaboratorOperator' },
  { value: 'admin', label: 'reports.transactions.admin' },
];

export const TYPE_TRANSACTION = [
  { value: 'Cash', label: 'reports.transactions.cash' },
  { value: 'Credit', label: 'reports.transactions.credit' },
  { value: 'Correction', label: 'reports.transactions.correction' },
];

export const TYPE_OPERATION = [
  { value: 'Deposit', label: 'reports.transactions.deposit' },
  { value: 'Withdrawal', label: 'reports.transactions.withdrawal' },
];

export const TYPE_CASH = [
  { value: 'Banking', label: 'reports.transactions.banking' },
  { value: 'Direct', label: 'reports.transactions.direct' },
];

export const amountOperators = [
  { value: '=', label: 'reports.transactions.equal' },
  { value: '!=', label: 'reports.transactions.notEqual' },
  { value: '<', label: 'reports.transactions.lessThan' },
  { value: '<=', label: 'reports.transactions.lessThanOrEqual' },
  { value: '>', label: 'reports.transactions.greaterThan' },
  { value: '>=', label: 'reports.transactions.greaterThanOrEqual' },
  { value: 'range', label: 'reports.transactions.range' },
];

const BADGE_COLOR = {
  YELLOW: '#FFD700',
  BROW: '#8E4148',
  GREEN: '#007E74',
  PURPLE: '#822691',
  ORANGE: '#FFA500',
  RED: '#FF6F61',
  DEEPSKYPE_BLUE: '#00bfff',
  GRAY: '#808080',
};

export enum BET_CASINO_STATUS {
  'ALEAPLAY_IN_PROGRESS' = 'IN_PROGRESS',
  'ALEAPLAY_COMPLETED' = 'COMPLETED',
  'ALEAPLAY_ROLLBACK' = 'ROLLBACK',
  'EVERY_MATRIX_OPEN' = 'open',
  'EVERY_MATRIX_CLOSED' = 'closed',
  'GROOVE_NOT_PROCESSED' = 'not processed',
  'GROOVE_PENDING' = 'pending',
  'GROOVE_COMPLETED' = 'completed',
  'GROOVE_ROLLBACK' = 'rollback',
}

const COMMON_TRANSLATIONS_CASINO = {
  COMPLETED: { es: 'Completado', en: 'Completed' },
  CLOSED: { es: 'Cerrado', en: 'Closed' },
  ROLLBACK: { es: 'Rollback', en: 'Rollback' },
  NOT_PROCESSED: { es: 'No procesado', en: 'Not processed' },
  IN_PROGRESS: { es: 'En progreso', en: 'In progress' },
};

const BET_CASINO_STATUS_TRANSLATIONS: Record<BET_CASINO_STATUS, TranslationsPerLanguage> = {
  [BET_CASINO_STATUS.ALEAPLAY_IN_PROGRESS]: COMMON_TRANSLATIONS_CASINO.IN_PROGRESS,
  [BET_CASINO_STATUS.ALEAPLAY_COMPLETED]: COMMON_TRANSLATIONS_CASINO.COMPLETED,
  [BET_CASINO_STATUS.ALEAPLAY_ROLLBACK]: COMMON_TRANSLATIONS_CASINO.ROLLBACK,
  [BET_CASINO_STATUS.EVERY_MATRIX_OPEN]: { es: 'Abierto', en: 'Open' },
  [BET_CASINO_STATUS.EVERY_MATRIX_CLOSED]: COMMON_TRANSLATIONS_CASINO.CLOSED,
  [BET_CASINO_STATUS.GROOVE_NOT_PROCESSED]: COMMON_TRANSLATIONS_CASINO.NOT_PROCESSED,
  [BET_CASINO_STATUS.GROOVE_PENDING]: { es: 'Pendiente', en: 'Pending' },
  [BET_CASINO_STATUS.GROOVE_COMPLETED]: COMMON_TRANSLATIONS_CASINO.COMPLETED,
  [BET_CASINO_STATUS.GROOVE_ROLLBACK]: COMMON_TRANSLATIONS_CASINO.ROLLBACK,
};

export const BET_CASINO_STATUS_BADGE_OPTIONS: Record<string, BadgeStyleConfig> = {
  [BET_CASINO_STATUS.ALEAPLAY_IN_PROGRESS]: {
    ...BET_CASINO_STATUS_TRANSLATIONS[BET_CASINO_STATUS.ALEAPLAY_IN_PROGRESS],
    color: BADGE_COLOR.YELLOW,
  },
  [BET_CASINO_STATUS.ALEAPLAY_COMPLETED]: {
    ...BET_CASINO_STATUS_TRANSLATIONS[BET_CASINO_STATUS.ALEAPLAY_COMPLETED],
    color: BADGE_COLOR.GREEN,
  },
  [BET_CASINO_STATUS.ALEAPLAY_ROLLBACK]: {
    ...BET_CASINO_STATUS_TRANSLATIONS[BET_CASINO_STATUS.ALEAPLAY_ROLLBACK],
    color: BADGE_COLOR.GRAY,
  },
  [BET_CASINO_STATUS.EVERY_MATRIX_OPEN]: {
    ...BET_CASINO_STATUS_TRANSLATIONS[BET_CASINO_STATUS.EVERY_MATRIX_OPEN],
    color: BADGE_COLOR.DEEPSKYPE_BLUE,
  },
  [BET_CASINO_STATUS.EVERY_MATRIX_CLOSED]: {
    ...BET_CASINO_STATUS_TRANSLATIONS[BET_CASINO_STATUS.EVERY_MATRIX_CLOSED],
    color: BADGE_COLOR.BROW,
  },
  [BET_CASINO_STATUS.GROOVE_NOT_PROCESSED]: {
    ...BET_CASINO_STATUS_TRANSLATIONS[BET_CASINO_STATUS.GROOVE_NOT_PROCESSED],
    color: BADGE_COLOR.RED,
  },
  [BET_CASINO_STATUS.GROOVE_PENDING]: {
    ...BET_CASINO_STATUS_TRANSLATIONS[BET_CASINO_STATUS.GROOVE_PENDING],
    color: BADGE_COLOR.ORANGE,
  },
  [BET_CASINO_STATUS.GROOVE_COMPLETED]: {
    ...BET_CASINO_STATUS_TRANSLATIONS[BET_CASINO_STATUS.GROOVE_COMPLETED],
    color: BADGE_COLOR.GREEN,
  },
  [BET_CASINO_STATUS.GROOVE_ROLLBACK]: {
    ...BET_CASINO_STATUS_TRANSLATIONS[BET_CASINO_STATUS.GROOVE_ROLLBACK],
    color: BADGE_COLOR.GRAY,
  },
};

export const OPTION_DEVICE_TYPE = [
  {
    value: 'Web',
    label: 'reports.loginHistory.webDevice',
  },
  {
    value: 'Mobile',
    label: 'reports.loginHistory.mobileDevice',
  },
  {
    value: 'Tablet',
    label: 'reports.loginHistory.tabletDevice',
  },
  {
    value: 'API',
    label: 'reports.loginHistory.apiDevice',
  },
  {
    value: 'Unknown',
    label: 'reports.loginHistory.unknownDevice',
  },
];

export const OPTION_TYPE_BONUS_DATE = [
  {
    value: 'expirationDate',
    label: 'reports.bonus.expirationDate',
  },
  {
    value: 'bonusClaimDate',
    label: 'reports.bonus.claimDate',
  },
  {
    value: 'bonusCreationDate',
    label: 'reports.bonus.createdAt',
  },
  {
    value: 'bonusAcceptanceDate',
    label: 'reports.bonus.acceptanceDate',
  },
  {
    value: 'bonusResultDate',
    label: 'reports.bonus.dateOfResult',
  },
  {
    value: 'bonusUpdateDate',
    label: 'reports.bonus.updateBonusRegisterDate',
  },
];

export const BONUS_STATE = [
  {
    value: 'Accepted',
    label: 'reports.bonus.stateAcepted',
  },
  {
    value: 'None',
    label: 'reports.bonus.stateNone',
  },
  {
    value: 'Converted',
    label: 'reports.bonus.stateConverted',
  },
  {
    value: 'Lost',
    label: 'reports.bonus.stateLost',
  },
  {
    value: 'Canceled',
    label: 'reports.bonus.stateCanceled',
  },
  {
    value: 'Expired',
    label: 'reports.bonus.stateExpired',
  },
];

export const Bonus_RESULT = {
  CONVERTED: 'Converted',
};

export const EXPORT_EXCEL = [
  { value: 'excel-all', text: 'words.exportAllExcel', icon: 'fa-file-excel' },
  {
    value: 'excel-range',
    text: 'words.exportRangeExcel',
    icon: 'fa-file-excel',
  },
  {
    value: 'excel-current',
    text: 'words.exportCurrentExcel',
    icon: 'fa-file-excel',
  },
  { value: 'csv-all', text: 'words.exportAllCSV', icon: 'fa-file-csv' },
  { value: 'csv-range', text: 'words.exportRangeCSV', icon: 'fa-file-csv' },
  { value: 'csv-current', text: 'words.exportCurrentCSV', icon: 'fa-file-csv' },
  { value: 'history', text: 'words.downloadHistory', icon: 'fa-history' },
];

export const EXPORT_OPTIONS = [
  {
    label: 'words.exportExcelAll',
    value: 'excel-range',
    icon: 'fa fa-file-excel',
    iconClass: 'text-success',
  },
  {
    label: 'words.exportExcelCurrent',
    value: 'excel-current',
    icon: 'fa fa-file-excel',
    iconClass: 'text-success',
  },
  {
    divider: true,
    label: '',
    value: null,
  },
  {
    label: 'words.exportCSVAll',
    value: 'csv-range',
    icon: 'fa fa-file-csv',
    iconClass: 'text-info',
  },
  {
    label: 'words.exportCSVCurrent',
    value: 'csv-current',
    icon: 'fa fa-file-csv',
    iconClass: 'text-info',
  },
];

export const FILTER_MAPPING_CONFIG: FilterMappingEntry[] = [
  { formField: 'betLocalId', apiField: 'betLocalId' },
  { formField: 'playerId', apiField: 'playerId' },
  { formField: 'playerName', apiField: 'username' },
  { formField: 'ipAddress', apiField: 'ipAddress' },
  { formField: 'statusEventSport', apiField: 'statusEventSport' },
  { formField: 'betType', apiField: 'betType' },
  { formField: 'result', apiField: 'result' },
  { formField: 'platform', apiField: 'platform' },
  { formField: 'firstValue', apiField: 'firstValue' },
  { formField: 'secondValue', apiField: 'secondValue' },
  { formField: 'condition', apiField: 'condition' },
  { formField: 'numericField', apiField: 'numericField' },
];

export const REPORT_STATUS = {
  COMPLETED: 'completed',
  FAILED: 'failed',
  IN_PROGRESS: 'inProgress',
};

export const REPORT_STATUS_OPTIONS: NgSelect<string>[] = [
  { label: 'words.all', value: '' },
  {
    label: 'reports.history.status.completed',
    value: REPORT_STATUS.COMPLETED,
  },
  {
    label: 'reports.history.status.failed',
    value: REPORT_STATUS.FAILED,
  },
  {
    label: 'reports.history.status.inProgress',
    value: REPORT_STATUS.IN_PROGRESS,
  },
];

export const TYPE_OPTIONS: NgSelect<string>[] = [
  { label: 'words.all', value: '' },
  { label: 'reports.history.exportByRange', value: 'range' },
  { label: 'reports.history.exportByLimit', value: 'limit' },
];

export const MODULE_OPTIONS: NgSelect<string>[] = [
  { label: 'words.all', value: '' },
  {
    label: 'words.sportsBetting',
    value: 'sports-betting',
  },
  { label: 'words.casino', value: 'casino' },
  {
    label: 'reports.liveCasino.title',
    value: 'live-casino',
  },
  { label: 'dashboard.filters.virtualGames', value: 'virtual' },
];

export const EXTENSION_OPTIONS: NgSelect<string>[] = [
  { label: 'words.all', value: '' },
  { label: 'btn.excel', value: 'excel' },
  { label: 'btn.csv', value: 'csv' },
];

export const REPORT_MODULES = {
  SPORTS_BETTING: 'sports-betting',
  CASINO: 'casino',
  CASINO_LIVE: 'casino-live',
  LOAD_WITHDRAWALS_AGENTS: 'load-withdrawals-agents',
  LOAD_WITHDRAWALS_PLAYERS: 'load-withdrawals-players',
  LOAD_WITHDRAWALS_COLLABORATORS: 'load-withdrawals-collaborators',
  LOGIN_HISTORY: 'login-history',
  GENERAL_PLAYERS: 'general-players',
  GENERAL_AGENTS: 'general-agents',
  GENERAL_PRODUCTS: 'general-products',
  LOGIN_HISTORIY_PLAYERS: 'login-history-players',
  TRANSACTION_AGENTS_BACKOFFICE: 'transaction-agents-backoffice',
  TRANSACTION_AGENTS_SORTICENTER: 'transaction-agents-sorticenter',
  TRANSACTION_PLAYERS_BACKOFFICE: 'transaction-players-backoffice',
  TRANSACTION_PLAYERS_SORTICENTER: 'transaction-players-sorticenter',
  BONUS: 'bonus',
  LOGIN_HISTORY_BACKOFFICE: 'login-history-backoffice',
};

export const INIT_FILTERS_REPORTS = [
  'operationType',
  'module',
  'userType',
  'playerId',
  'username',
  'betType',
  'result',
  'platform',
  'currency',
  'isBonus',
  'type',
];

export const OPTION_LOGIN_STATUS = [
  {
    value: true,
    label: 'reports.loginHistory.success',
  },
  {
    value: false,
    label: 'reports.loginHistory.failed',
  },
];

export const USER_TYPE_LABELS: Record<string, string> = {
  agent: 'reports.loginHistory.userTypeAgent',
  admin: 'reports.loginHistory.userTypeAdmin',
  collaboratorOperator: 'reports.loginHistory.userTypeCollaborator',
  collaboratorAgent: 'reports.loginHistory.userTypeCollaboratorAgent',
  player: 'reports.loginHistory.userTypePlayer',
  cashdesks: 'reports.loginHistory.userTypeCashdesk',
  affiliate: 'reports.loginHistory.userTypeAffiliate',
  allCollaborator: 'reports.loginHistory.userTypeAllCollaborator',
  playerAffiliate: 'reports.loginHistory.userTypePlayerAffiliate',
};

export const OPTION_DUPLICATE_BY = [
  {
    value: 'ipAddress',
    label: 'reports.loginHistory.duplicateByIp',
  },
  {
    value: 'deviceTokenHash',
    label: 'reports.loginHistory.duplicateByDeviceToken',
  },
  {
    value: 'browserFingerprintHash',
    label: 'reports.loginHistory.userAgentHash',
  },
];
